import { ChronoUnit, Instant, LocalDate, ZonedDateTime, ZoneId } from "@js-joda/core";
import { toZonedDateTimeOrNow } from "./time-utils";
import { DweenCalendarEnum } from "./enums";

export const getFiscalYearStart = (date: ZonedDateTime): ZonedDateTime => {
  const year = date.year();
  const candidate = ZonedDateTime.of(year - 1, 12, 30, 0, 0, 0, 0, date.zone());
  if (date.isBefore(candidate)) {
    return candidate.minusYears(1);
  }
  return candidate;
};

export const getMonthRangeIn445Calendar = (referenceTime: Instant, zoneId: ZoneId) => {
  const ref = toZonedDateTimeOrNow(zoneId, referenceTime);
  const fiscalYearStart = getFiscalYearStart(ref);

  const pattern = [28, 28, 35];

  const daysSinceFiscalStart = fiscalYearStart.until(ref, ChronoUnit.DAYS);
  let daySum = 0;
  let fiscalMonth = 0;

  while (fiscalMonth < 12) {
    const monthLength = pattern[fiscalMonth % 3];
    if (daysSinceFiscalStart < daySum + monthLength) break;
    daySum += monthLength;
    fiscalMonth++;
  }

  const startOfMonth = fiscalYearStart.plusDays(daySum);
  const endOfMonth = startOfMonth.plusDays(pattern[fiscalMonth % 3]).minusDays(1);

  return [startOfMonth, endOfMonth];
};

export const getFirstDayOfCalendarMonthFrom445 = (refInstant: Instant, tz: ZoneId): ZonedDateTime => {
  const info = get445MonthInfo(refInstant, tz);
  const start = info.startDate;
  const end = info.endDate;

  const startMonth = start.monthValue();
  const endMonth = end.monthValue();

  let dominantMonth = startMonth;

  if (startMonth !== endMonth) {
    const nextMonthFirstDay = LocalDate.of(
      start.year() + Math.floor(startMonth / 12),
      (startMonth % 12) + 1,
      1
    );

    const daysInStartMonth = nextMonthFirstDay.minusDays(1).dayOfMonth() - start.dayOfMonth() + 1;
    const totalDays = start.until(end, ChronoUnit.DAYS) + 1;
    const daysInEndMonth = totalDays - daysInStartMonth;

    dominantMonth = daysInEndMonth > daysInStartMonth ? endMonth : startMonth;
  }

  const firstOfCalendarMonth = LocalDate.of(end.year(), dominantMonth, 1);
  return firstOfCalendarMonth.atStartOfDay(tz);
};

export const get445MonthInfo = (
  refInstant: Instant,
  tz: ZoneId
): { startDate: LocalDate; endDate: LocalDate } => {
  const refZdt = refInstant.atZone(tz);
  const fiscalYearStart = getFiscalYearStart(refZdt).toLocalDate();

  const periodDays = [28, 28, 35, 28, 28, 35, 28, 28, 35, 28, 28, 35];

  const nextFiscalStart = fiscalYearStart.plusWeeks(52);
  const extraWeek = !refZdt.toLocalDate().isBefore(nextFiscalStart.plusDays(1)) ? 7 : 0;

  if (extraWeek) {
    periodDays.push(extraWeek);
  }

  let running = fiscalYearStart;
  for (let i = 0; i < periodDays.length; i++) {
    const start = running;
    const end = start.plusDays(periodDays[i] - 1);
    if (!refZdt.toLocalDate().isBefore(start) && !refZdt.toLocalDate().isAfter(end)) {
      return { startDate: start, endDate: end };
    }
    running = start.plusDays(periodDays[i]);
  }

  const lastStart = fiscalYearStart.plusDays(periodDays.slice(0, -1).reduce((a, b) => a + b, 0));
  return { startDate: lastStart, endDate: lastStart.plusDays(periodDays[periodDays.length - 1] - 1) };
};

export const getAdjustedReferenceTime = (
  refTime: Instant,
  tz: ZoneId,
  calendarType?: DweenCalendarEnum,
  useDataUpToLastFinishedMonth?: boolean,
  useDataUpToLastFinishedWeek?: boolean
) => {
  const baseDateTime =
    calendarType === DweenCalendarEnum.CALENDAR_445
      ? getFirstDayOfCalendarMonthFrom445(refTime ?? Instant.now(), tz)
      : toZonedDateTimeOrNow(tz, refTime);

  if (useDataUpToLastFinishedMonth) {
    return baseDateTime.minusMonths(1).toInstant();
  }
  if (useDataUpToLastFinishedWeek) {
    return baseDateTime.minusWeeks(1).toInstant();
  }
  return baseDateTime.toInstant();
};
